import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TablePagination, Button, TextField, MenuItem,
  Select, FormControl, InputLabel, IconButton, Chip, Dialog, DialogTitle,
  DialogContent, DialogActions, Grid, Card, CardContent, Tabs, Tab, Avatar,
  CircularProgress, Snackbar, Alert, Divider, InputAdornment
} from '@mui/material';
import { Visibility, Check, Close, Search, Block, Download, Refresh, Lock, Visibility as VisibilityIcon, VisibilityOff, Delete } from '@mui/icons-material';
import axios from 'axios';

const API_BASE = 'https://api.bharatdarshan.app/api/vendor/admin';

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return { Authorization: `Bearer ${token}` };
};

const VendorManagement = ({ initialTab } = {}) => {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [kycFilter, setKycFilter] = useState(initialTab === 'pending' ? 'PENDING' : 'all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [detailDialog, setDetailDialog] = useState(false);
  const [actionDialog, setActionDialog] = useState({ open: false, type: '', vendor: null });
  const [rejectReason, setRejectReason] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [actionLoading, setActionLoading] = useState(false);

  // Password change state
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);

  const [stats, setStats] = useState({ total: 0, pending: 0, approved: 0, rejected: 0, suspended: 0 });

  const categories = ['ecommerce', 'astrologer', 'rojgar', 'pandit', 'Electronics', 'Fashion', 'Home Decor', 'Food & Grocery', 'Books', 'Health & Beauty', 'Sports', 'Handicrafts', 'Religious Items'];
  const rejectReasons = ['Invalid Documents', 'Incomplete Business Details', 'Document Quality Poor', 'Bank Details Mismatch', 'Suspicious Activity', 'Other'];

  // Fetch vendors from API
  const fetchVendors = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/all`);
      const result = await response.json();
      if (result.success && result.data) {
        const vendorData = result.data.map(v => ({
          ...v,
          kycStatus: v.kycStatus ? v.kycStatus.toUpperCase() : 'PENDING',
          vendorStatus: v.vendorStatus ? v.vendorStatus.toUpperCase() : 'INACTIVE',
          documents: {
            pan: !!v.panNumber || !!v.panCardImage,
            gst: !!v.gstNumber,
            bank: !!v.bankAccountNumber,
            aadhar: !!v.aadharNumber || !!v.aadharFrontImage
          },
          category: v.vendorType || 'General',
          products: v.products || 0,
          orders: v.orders || 0,
          revenue: v.revenue || 0
        }));
        setVendors(vendorData);
        
        const newStats = {
          total: vendorData.length,
          pending: vendorData.filter(v => v.kycStatus === 'PENDING' || v.kycStatus === 'SUBMITTED').length,
          approved: vendorData.filter(v => v.kycStatus === 'APPROVED').length,
          rejected: vendorData.filter(v => v.kycStatus === 'REJECTED').length,
          suspended: vendorData.filter(v => v.vendorStatus === 'SUSPENDED').length
        };
        setStats(newStats);
      }
    } catch (error) {
      console.error('Error fetching vendors:', error);
      setSnackbar({ open: true, message: 'Failed to load vendors', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchVendors(); }, []);

  const getKycColor = (status) => {
    switch (status) {
      case 'PENDING': case 'SUBMITTED': return 'warning';
      case 'APPROVED': return 'success';
      case 'REJECTED': return 'error';
      default: return 'default';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'ACTIVE': return 'success';
      case 'INACTIVE': return 'default';
      case 'SUSPENDED': return 'error';
      default: return 'default';
    }
  };

  const handleApproveKYC = (vendor) => setActionDialog({ open: true, type: 'approve', vendor });
  const handleRejectKYC = (vendor) => setActionDialog({ open: true, type: 'reject', vendor });
  const handleSuspend = (vendor) => setActionDialog({ open: true, type: 'suspend', vendor });
  const handleActivate = (vendor) => setActionDialog({ open: true, type: 'activate', vendor });
  const handleDeleteVendor = (vendor) => setActionDialog({ open: true, type: 'delete', vendor });

  const confirmAction = async () => {
    const { type, vendor } = actionDialog;
    setActionLoading(true);
    try {
      let endpoint = '';
      let body = {};
      switch (type) {
        case 'approve': endpoint = `${API_BASE}/approve/${vendor._id}`; body = { adminNotes }; break;
        case 'reject': endpoint = `${API_BASE}/reject/${vendor._id}`; body = { rejectionReason: rejectReason, adminNotes }; break;
        case 'suspend': endpoint = `${API_BASE}/suspend/${vendor._id}`; body = { adminNotes }; break;
        case 'activate': endpoint = `${API_BASE}/approve/${vendor._id}`; body = { adminNotes }; break;
        case 'delete': 
          try {
            const delRes = await fetch(`${API_BASE}/${vendor._id}`, { method: 'DELETE', headers: { 'Content-Type': 'application/json' } });
            const delResult = await delRes.json();
            if (delResult.success) {
              setSnackbar({ open: true, message: 'Vendor deleted successfully. Customer account preserved.', severity: 'success' });
              await fetchVendors();
            } else {
              setSnackbar({ open: true, message: delResult.message || 'Failed to delete vendor', severity: 'error' });
            }
          } catch(e) {
            setSnackbar({ open: true, message: 'Failed to delete vendor', severity: 'error' });
          }
          setActionLoading(false);
          setActionDialog({ open: false, type: '', vendor: null });
          setRejectReason('');
          setAdminNotes('');
          return;
        default: return;
      }
      const response = await fetch(endpoint, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      const result = await response.json();
      if (result.success) {
        setSnackbar({ open: true, message: `Vendor ${type}d successfully`, severity: 'success' });
        await fetchVendors();
      } else {
        setSnackbar({ open: true, message: result.message || `Failed to ${type} vendor`, severity: 'error' });
      }
    } catch (error) {
      console.error(`Error ${type}ing vendor:`, error);
      setSnackbar({ open: true, message: `Failed to ${type} vendor`, severity: 'error' });
    } finally {
      setActionLoading(false);
      setActionDialog({ open: false, type: '', vendor: null });
      setRejectReason('');
      setAdminNotes('');
    }
  };

  const handleChangePassword = async () => {
    if (!selectedVendor) return;
    if (!newPassword || newPassword.length < 6) {
      setSnackbar({ open: true, message: 'Password must be at least 6 characters', severity: 'warning' });
      return;
    }
    if (newPassword !== confirmPassword) {
      setSnackbar({ open: true, message: 'Passwords do not match', severity: 'warning' });
      return;
    }
    setPasswordLoading(true);
    try {
      const res = await axios.put(
        `https://api.bharatdarshan.app/api/vendor/admin/password/${selectedVendor._id}`,
        { newPassword },
        { headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' } }
      );
      if (res.data.success) {
        setSnackbar({ open: true, message: 'Password changed successfully', severity: 'success' });
        setNewPassword('');
        setConfirmPassword('');
      } else {
        setSnackbar({ open: true, message: res.data.message || 'Failed to change password', severity: 'error' });
      }
    } catch (err) {
      console.error('Error changing password:', err);
      setSnackbar({ open: true, message: err.response?.data?.message || 'Failed to change password', severity: 'error' });
    } finally {
      setPasswordLoading(false);
    }
  };

  const filteredVendors = vendors.filter(v => {
    const matchesSearch = (v.businessName || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                          (v.ownerName || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                          (v.phone || '').includes(searchQuery);
    const matchesKyc = kycFilter === 'all' || v.kycStatus === kycFilter ||
                       (kycFilter === 'PENDING' && v.kycStatus === 'SUBMITTED');
    const matchesStatus = statusFilter === 'all' || v.vendorStatus === statusFilter;
    const matchesCategory = categoryFilter === 'all' || v.category === categoryFilter || v.vendorType === categoryFilter;
    return matchesSearch && matchesKyc && matchesStatus && matchesCategory;
  });

  const formatCurrency = (amt) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amt || 0);
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  // Helper: humanize camelCase / snake_case keys
  const humanize = (k) => k.replace(/([A-Z])/g, ' $1').replace(/[_-]/g, ' ').replace(/^./, s => s.toUpperCase()).trim();

  // Helper: render a single primitive value
  const renderValue = (v) => {
    if (v === null || v === undefined || v === '') return 'N/A';
    if (typeof v === 'boolean') return v ? 'Yes' : 'No';
    if (typeof v === 'number') return String(v);
    if (typeof v === 'string') {
      // Detect URLs / image paths
      if (/^https?:\/\//.test(v)) {
        if (/\.(jpg|jpeg|png|gif|webp)$/i.test(v)) {
          return <img src={v} alt="" style={{ maxWidth: 120, maxHeight: 80, borderRadius: 4, cursor: 'pointer' }} onClick={() => window.open(v, '_blank')} />;
        }
        if (/\.(mp4|mov|webm)$/i.test(v)) {
          return <video controls style={{ maxWidth: 200, maxHeight: 120 }}><source src={v} /></video>;
        }
        return <a href={v} target="_blank" rel="noreferrer" style={{ color: '#2196F3', wordBreak: 'break-all' }}>{v}</a>;
      }
      // ISO date
      if (/^\d{4}-\d{2}-\d{2}T/.test(v)) {
        try { return new Date(v).toLocaleString('en-IN'); } catch(e) {}
      }
      return v;
    }
    return String(v);
  };

  // Render all vendor fields for "Full Profile" tab - human readable, no raw JSON
  const renderFullProfile = (vendor) => {
    if (!vendor) return null;
    const excludeKeys = ['__v', '_id', 'password', 'otp', 'otpExpiry', 'updatedAt'];
    const entries = Object.entries(vendor).filter(([key]) => !excludeKeys.includes(key));
    return (
      <Grid container spacing={1}>
        {entries.map(([key, value]) => {
          // Object: render as nested rows
          if (value && typeof value === 'object' && !Array.isArray(value)) {
            const subEntries = Object.entries(value).filter(([k]) => !excludeKeys.includes(k));
            if (subEntries.length === 0) return null;
            return (
              <Grid item xs={12} key={key}>
                <Paper sx={{ p: 1.5, bgcolor: '#f5f5f5' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#4CAF50', mb: 1 }}>{humanize(key)}</Typography>
                  <Grid container spacing={1}>
                    {subEntries.map(([sk, sv]) => (
                      <Grid item xs={12} sm={6} key={sk}>
                        <Typography variant="caption" color="textSecondary" sx={{ display: 'block', fontSize: 10 }}>{humanize(sk)}</Typography>
                        <Typography variant="body2" component="div" sx={{ wordBreak: 'break-word' }}>
                          {(sv && typeof sv === 'object') ? Object.entries(sv).map(([k2,v2]) => <div key={k2}><b>{humanize(k2)}:</b> {renderValue(v2)}</div>) : renderValue(sv)}
                        </Typography>
                      </Grid>
                    ))}
                  </Grid>
                </Paper>
              </Grid>
            );
          }
          // Array
          if (Array.isArray(value)) {
            if (value.length === 0) return null;
            return (
              <Grid item xs={12} key={key}>
                <Paper sx={{ p: 1.5, bgcolor: '#f5f5f5' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#4CAF50', mb: 1 }}>{humanize(key)} ({value.length})</Typography>
                  {value.map((it, i) => (
                    <Box key={i} sx={{ pl: 1, py: 0.5, borderLeft: '2px solid #ddd', mb: 0.5 }}>
                      {(it && typeof it === 'object') ? Object.entries(it).filter(([k]) => !excludeKeys.includes(k)).map(([k,v]) => <Typography key={k} variant="body2"><b>{humanize(k)}:</b> {renderValue(v)}</Typography>) : <Typography variant="body2">{renderValue(it)}</Typography>}
                    </Box>
                  ))}
                </Paper>
              </Grid>
            );
          }
          // Primitive
          return (
            <Grid item xs={12} sm={6} key={key}>
              <Paper sx={{ p: 1.5, bgcolor: '#f9f9f9' }}>
                <Typography variant="caption" color="textSecondary" sx={{ textTransform: 'uppercase', fontSize: 10 }}>{humanize(key)}</Typography>
                <Typography variant="body2" component="div" sx={{ wordBreak: 'break-word', mt: 0.5 }}>{renderValue(value)}</Typography>
              </Paper>
            </Grid>
          );
        })}
      </Grid>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#4CAF50' }}>Vendor Management</Typography>
        <Button variant="outlined" startIcon={<Refresh />} onClick={fetchVendors} disabled={loading}>Refresh</Button>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {[
          { label: 'Total Vendors', value: stats.total, color: '#2196F3' },
          { label: 'Pending KYC', value: stats.pending, color: '#FF9800' },
          { label: 'Approved', value: stats.approved, color: '#4CAF50' },
          { label: 'Suspended', value: stats.suspended, color: '#F44336' }
        ].map((stat, i) => (
          <Grid item xs={12} sm={6} md={3} key={i}>
            <Card sx={{ bgcolor: stat.color, color: 'white' }}>
              <CardContent>
                <Typography variant="h4" sx={{ fontWeight: 'bold' }}>{stat.value}</Typography>
                <Typography>{stat.label}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField fullWidth size="small" placeholder="Search vendors..." value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{ startAdornment: <Search sx={{ mr: 1, color: 'gray' }} /> }} />
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>KYC Status</InputLabel>
              <Select value={kycFilter} label="KYC Status" onChange={(e) => setKycFilter(e.target.value)}>
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="PENDING">Pending</MenuItem>
                <MenuItem value="APPROVED">Approved</MenuItem>
                <MenuItem value="REJECTED">Rejected</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="ACTIVE">Active</MenuItem>
                <MenuItem value="INACTIVE">Inactive</MenuItem>
                <MenuItem value="SUSPENDED">Suspended</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Vendor Type</InputLabel>
              <Select value={categoryFilter} label="Vendor Type" onChange={(e) => setCategoryFilter(e.target.value)}>
                <MenuItem value="all">All Types</MenuItem>
                {categories.map(cat => <MenuItem key={cat} value={cat}>{cat}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button variant="outlined" startIcon={<Download />} fullWidth>Export</Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Table */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead sx={{ bgcolor: '#4CAF50' }}>
              <TableRow>
                {['Vendor', 'Type', 'City', 'Phone', 'KYC', 'Status', 'Created', 'Actions'].map(h => (
                  <TableCell key={h} sx={{ color: 'white', fontWeight: 'bold' }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredVendors.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                    <Typography color="textSecondary">No vendors found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredVendors.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((vendor) => (
                  <TableRow key={vendor._id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ bgcolor: '#4CAF50' }}>{(vendor.businessName || 'V')[0]}</Avatar>
                        <Box>
                          <Typography fontWeight="bold">{vendor.businessName || 'N/A'}</Typography>
                          <Typography variant="caption" color="textSecondary">{vendor.ownerName || ''}</Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell><Chip label={vendor.vendorType || 'General'} size="small" /></TableCell>
                    <TableCell>{vendor.city || 'N/A'}</TableCell>
                    <TableCell>{vendor.phone || 'N/A'}</TableCell>
                    <TableCell><Chip label={vendor.kycStatus} color={getKycColor(vendor.kycStatus)} size="small" /></TableCell>
                    <TableCell><Chip label={vendor.vendorStatus} color={getStatusColor(vendor.vendorStatus)} size="small" /></TableCell>
                    <TableCell>{formatDate(vendor.createdAt)}</TableCell>
                    <TableCell>
                      <IconButton color="primary" onClick={() => { setSelectedVendor(vendor); setTabValue(0); setDetailDialog(true); setNewPassword(''); setConfirmPassword(''); }}>
                        <Visibility />
                      </IconButton>
                      {(vendor.kycStatus === 'PENDING' || vendor.kycStatus === 'SUBMITTED') && (
                        <>
                          <IconButton color="success" onClick={() => handleApproveKYC(vendor)}><Check /></IconButton>
                          <IconButton color="error" onClick={() => handleRejectKYC(vendor)}><Close /></IconButton>
                        </>
                      )}
                      {vendor.vendorStatus === 'ACTIVE' && <IconButton color="warning" onClick={() => handleSuspend(vendor)}><Block /></IconButton>}
                      {vendor.vendorStatus === 'SUSPENDED' && <IconButton color="success" onClick={() => handleActivate(vendor)}><Check /></IconButton>}
                      <IconButton color="error" title="Delete Vendor" onClick={() => handleDeleteVendor(vendor)}><Delete /></IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <TablePagination rowsPerPageOptions={[5, 10, 25]} component="div"
            count={filteredVendors.length} rowsPerPage={rowsPerPage} page={page}
            onPageChange={(e, p) => setPage(p)}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }} />
        </TableContainer>
      )}

      {/* Detail Dialog - Enhanced with Account & Full Profile tabs */}
      <Dialog open={detailDialog} onClose={() => setDetailDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor: '#4CAF50', color: 'white' }}>
          Vendor Details - {selectedVendor?.businessName}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {selectedVendor && (
            <>
              <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ mb: 2 }} variant="scrollable" scrollButtons="auto">
                <Tab label="Business Info" />
                <Tab label="Documents" />
                <Tab label="Bank Details" />
                <Tab label="Account" />
                <Tab label="Full Profile" />
              </Tabs>

              {/* Tab 0: Business Info */}
              {tabValue === 0 && (
                <Grid container spacing={2}>
                  <Grid item xs={6}><Typography><b>Business Name:</b> {selectedVendor.businessName || 'N/A'}</Typography></Grid>
                  <Grid item xs={6}><Typography><b>Owner:</b> {selectedVendor.ownerName || 'N/A'}</Typography></Grid>
                  <Grid item xs={6}><Typography><b>Phone:</b> {selectedVendor.phone || 'N/A'}</Typography></Grid>
                  <Grid item xs={6}><Typography><b>Email:</b> {selectedVendor.email || 'N/A'}</Typography></Grid>
                  <Grid item xs={6}><Typography><b>Vendor Type:</b> {selectedVendor.vendorType || 'N/A'}</Typography></Grid>
                  <Grid item xs={6}><Typography><b>Address:</b> {selectedVendor.address || 'N/A'}</Typography></Grid>
                  <Grid item xs={6}><Typography><b>City:</b> {selectedVendor.city || 'N/A'}</Typography></Grid>
                  <Grid item xs={6}><Typography><b>State:</b> {selectedVendor.state || 'N/A'}</Typography></Grid>
                  <Grid item xs={6}><Typography><b>Pincode:</b> {selectedVendor.pincode || 'N/A'}</Typography></Grid>
                  <Grid item xs={6}><Typography><b>Created:</b> {formatDate(selectedVendor.createdAt)}</Typography></Grid>
                  <Grid item xs={6}><Typography><b>KYC Status:</b> <Chip label={selectedVendor.kycStatus} color={getKycColor(selectedVendor.kycStatus)} size="small" /></Typography></Grid>
                  <Grid item xs={6}><Typography><b>Vendor Status:</b> <Chip label={selectedVendor.vendorStatus} color={getStatusColor(selectedVendor.vendorStatus)} size="small" /></Typography></Grid>
                  {selectedVendor.rejectionReason && (
                    <Grid item xs={12}><Typography color="error"><b>Rejection Reason:</b> {selectedVendor.rejectionReason}</Typography></Grid>
                  )}
                  {selectedVendor.adminNotes && (
                    <Grid item xs={12}><Typography><b>Admin Notes:</b> {selectedVendor.adminNotes}</Typography></Grid>
                  )}
                </Grid>
              )}

              {/* Tab 1: Documents */}
              {tabValue === 1 && (
                <Grid container spacing={2}>
                  <Grid item xs={6} md={3}>
                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="subtitle2">PAN</Typography>
                      <Chip label={selectedVendor.panNumber ? 'Provided' : 'Missing'} color={selectedVendor.panNumber ? 'success' : 'error'} sx={{ mt: 1 }} />
                      {selectedVendor.panNumber && <Typography variant="caption" display="block">{selectedVendor.panNumber}</Typography>}
                      {selectedVendor.panCardImage && (
                        <Box sx={{ mt: 1 }}>
                          <img src={selectedVendor.panCardImage.startsWith('http') ? selectedVendor.panCardImage : 'https://api.bharatdarshan.app/' + selectedVendor.panCardImage} alt="PAN Card" style={{ width: '100%', maxHeight: 120, objectFit: 'contain', borderRadius: 4, cursor: 'pointer' }} onClick={() => window.open(selectedVendor.panCardImage.startsWith('http') ? selectedVendor.panCardImage : 'https://api.bharatdarshan.app/' + selectedVendor.panCardImage, '_blank')} />
                        </Box>
                      )}
                    </Paper>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="subtitle2">GST</Typography>
                      <Chip label={selectedVendor.gstNumber ? 'Provided' : 'Missing'} color={selectedVendor.gstNumber ? 'success' : 'error'} sx={{ mt: 1 }} />
                      {selectedVendor.gstNumber && <Typography variant="caption" display="block">{selectedVendor.gstNumber}</Typography>}
                    </Paper>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="subtitle2">AADHAR (Front)</Typography>
                      <Chip label={selectedVendor.aadharNumber ? 'Provided' : 'Missing'} color={selectedVendor.aadharNumber ? 'success' : 'error'} sx={{ mt: 1 }} />
                      {selectedVendor.aadharNumber && <Typography variant="caption" display="block">{selectedVendor.aadharNumber}</Typography>}
                      {selectedVendor.aadharFrontImage && (
                        <Box sx={{ mt: 1 }}>
                          <img src={selectedVendor.aadharFrontImage.startsWith('http') ? selectedVendor.aadharFrontImage : 'https://api.bharatdarshan.app/' + selectedVendor.aadharFrontImage} alt="Aadhar Front" style={{ width: '100%', maxHeight: 120, objectFit: 'contain', borderRadius: 4, cursor: 'pointer' }} onClick={() => window.open(selectedVendor.aadharFrontImage.startsWith('http') ? selectedVendor.aadharFrontImage : 'https://api.bharatdarshan.app/' + selectedVendor.aadharFrontImage, '_blank')} />
                        </Box>
                      )}
                    </Paper>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="subtitle2">AADHAR (Back)</Typography>
                      <Chip label={selectedVendor.aadharBackImage ? 'Provided' : 'Missing'} color={selectedVendor.aadharBackImage ? 'success' : 'error'} sx={{ mt: 1 }} />
                      {selectedVendor.aadharBackImage && (
                        <Box sx={{ mt: 1 }}>
                          <img src={selectedVendor.aadharBackImage.startsWith('http') ? selectedVendor.aadharBackImage : 'https://api.bharatdarshan.app/' + selectedVendor.aadharBackImage} alt="Aadhar Back" style={{ width: '100%', maxHeight: 120, objectFit: 'contain', borderRadius: 4, cursor: 'pointer' }} onClick={() => window.open(selectedVendor.aadharBackImage.startsWith('http') ? selectedVendor.aadharBackImage : 'https://api.bharatdarshan.app/' + selectedVendor.aadharBackImage, '_blank')} />
                        </Box>
                      )}
                    </Paper>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="subtitle2">Profile Photo</Typography>
                      <Chip label={selectedVendor.profilePhoto ? 'Provided' : 'Missing'} color={selectedVendor.profilePhoto ? 'success' : 'error'} sx={{ mt: 1 }} />
                      {selectedVendor.profilePhoto && (
                        <Box sx={{ mt: 1 }}>
                          <img src={selectedVendor.profilePhoto.startsWith('http') ? selectedVendor.profilePhoto : 'https://api.bharatdarshan.app/' + selectedVendor.profilePhoto} alt="Profile" style={{ width: '100%', maxHeight: 120, objectFit: 'contain', borderRadius: 4, cursor: 'pointer' }} onClick={() => window.open(selectedVendor.profilePhoto.startsWith('http') ? selectedVendor.profilePhoto : 'https://api.bharatdarshan.app/' + selectedVendor.profilePhoto, '_blank')} />
                        </Box>
                      )}
                    </Paper>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="subtitle2">Bank Account</Typography>
                      <Chip label={selectedVendor.bankAccountNumber ? 'Provided' : 'Missing'} color={selectedVendor.bankAccountNumber ? 'success' : 'error'} sx={{ mt: 1 }} />
                    </Paper>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="subtitle2">Live Selfie</Typography>
                      <Chip label={selectedVendor.selfieImage ? 'Provided' : 'Missing'} color={selectedVendor.selfieImage ? 'success' : 'error'} sx={{ mt: 1 }} />
                      {selectedVendor.selfieImage && (
                        <Box sx={{ mt: 1 }}>
                          <img src={selectedVendor.selfieImage.startsWith('http') ? selectedVendor.selfieImage : 'https://api.bharatdarshan.app/' + selectedVendor.selfieImage} alt="Selfie" style={{ width: '100%', maxHeight: 120, objectFit: 'contain', borderRadius: 4, cursor: 'pointer' }} onClick={() => window.open(selectedVendor.selfieImage.startsWith('http') ? selectedVendor.selfieImage : 'https://api.bharatdarshan.app/' + selectedVendor.selfieImage, '_blank')} />
                        </Box>
                      )}
                    </Paper>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="subtitle2">GST Certificate</Typography>
                      <Chip label={selectedVendor.gstCertificateImage ? 'Provided' : 'Missing'} color={selectedVendor.gstCertificateImage ? 'success' : 'default'} sx={{ mt: 1 }} />
                      {selectedVendor.gstCertificateImage && (
                        <Box sx={{ mt: 1 }}>
                          <img src={selectedVendor.gstCertificateImage.startsWith('http') ? selectedVendor.gstCertificateImage : 'https://api.bharatdarshan.app/' + selectedVendor.gstCertificateImage} alt="GST Certificate" style={{ width: '100%', maxHeight: 120, objectFit: 'contain', borderRadius: 4, cursor: 'pointer' }} onClick={() => window.open(selectedVendor.gstCertificateImage.startsWith('http') ? selectedVendor.gstCertificateImage : 'https://api.bharatdarshan.app/' + selectedVendor.gstCertificateImage, '_blank')} />
                        </Box>
                      )}
                    </Paper>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="subtitle2">Bank Passbook</Typography>
                      <Chip label={selectedVendor.bankPassbookImage ? 'Provided' : 'Missing'} color={selectedVendor.bankPassbookImage ? 'success' : 'default'} sx={{ mt: 1 }} />
                      {selectedVendor.bankPassbookImage && (
                        <Box sx={{ mt: 1 }}>
                          <img src={selectedVendor.bankPassbookImage.startsWith('http') ? selectedVendor.bankPassbookImage : 'https://api.bharatdarshan.app/' + selectedVendor.bankPassbookImage} alt="Bank Passbook" style={{ width: '100%', maxHeight: 120, objectFit: 'contain', borderRadius: 4, cursor: 'pointer' }} onClick={() => window.open(selectedVendor.bankPassbookImage.startsWith('http') ? selectedVendor.bankPassbookImage : 'https://api.bharatdarshan.app/' + selectedVendor.bankPassbookImage, '_blank')} />
                        </Box>
                      )}
                    </Paper>
                  </Grid>

                  {/* Premises Video */}
                  <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 2 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>🎥 Premises Video</Typography>
                      <Chip label={selectedVendor.kitchenVideoUrl ? 'Provided' : 'Not uploaded'} color={selectedVendor.kitchenVideoUrl ? 'success' : 'default'} sx={{ mb: 1 }} />
                      {selectedVendor.kitchenVideoUrl ? (
                        <Box sx={{ mt: 1 }}>
                          <video
                            controls
                            preload="metadata"
                            style={{ width: '100%', maxHeight: 280, background: '#000', borderRadius: 6 }}
                            src={selectedVendor.kitchenVideoUrl.startsWith('http') ? selectedVendor.kitchenVideoUrl : 'https://api.bharatdarshan.app/' + selectedVendor.kitchenVideoUrl}
                          >
                            Your browser does not support video playback.
                          </video>
                          <Box sx={{ mt: 1, textAlign: 'right' }}>
                            <Button size="small" variant="outlined" onClick={() => window.open(selectedVendor.kitchenVideoUrl.startsWith('http') ? selectedVendor.kitchenVideoUrl : 'https://api.bharatdarshan.app/' + selectedVendor.kitchenVideoUrl, '_blank')}>Open in new tab</Button>
                          </Box>
                        </Box>
                      ) : (
                        <Typography variant="caption" color="textSecondary">Vendor has not uploaded a premises video.</Typography>
                      )}
                    </Paper>
                  </Grid>

                  {/* Business Location */}
                  <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 2 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>📍 Business Location</Typography>
                      {selectedVendor.location && selectedVendor.location.latitude && selectedVendor.location.longitude ? (
                        <>
                          <Typography variant="body2"><b>Latitude:</b> {selectedVendor.location.latitude}</Typography>
                          <Typography variant="body2"><b>Longitude:</b> {selectedVendor.location.longitude}</Typography>
                          {selectedVendor.location.address ? (
                            <Typography variant="body2" sx={{ mt: 0.5 }}><b>Address:</b> {selectedVendor.location.address}</Typography>
                          ) : null}
                          <Box sx={{ mt: 1, height: 240, borderRadius: 6, overflow: 'hidden', border: '1px solid #ddd' }}>
                            <iframe
                              title="Vendor Location Map"
                              width="100%"
                              height="100%"
                              style={{ border: 0 }}
                              loading="lazy"
                              src={`https://www.openstreetmap.org/export/embed.html?bbox=${selectedVendor.location.longitude-0.005}%2C${selectedVendor.location.latitude-0.005}%2C${selectedVendor.location.longitude+0.005}%2C${selectedVendor.location.latitude+0.005}&layer=mapnik&marker=${selectedVendor.location.latitude}%2C${selectedVendor.location.longitude}`}
                            />
                          </Box>
                          <Box sx={{ mt: 1, textAlign: 'right' }}>
                            <Button size="small" variant="contained" color="primary" onClick={() => window.open(`https://www.google.com/maps?q=${selectedVendor.location.latitude},${selectedVendor.location.longitude}`, '_blank')}>Open in Google Maps</Button>
                          </Box>
                        </>
                      ) : (
                        <Typography variant="caption" color="textSecondary">Vendor has not pinned a location.</Typography>
                      )}
                    </Paper>
                  </Grid>
                </Grid>
              )}

              {/* Tab 2: Bank Details */}
              {tabValue === 2 && (
                <Grid container spacing={2}>
                  <Grid item xs={6}><Typography><b>Account Holder:</b> {selectedVendor.accountHolderName || 'N/A'}</Typography></Grid>
                  <Grid item xs={6}><Typography><b>Account Number:</b> {selectedVendor.bankAccountNumber || 'N/A'}</Typography></Grid>
                  <Grid item xs={6}><Typography><b>IFSC Code:</b> {selectedVendor.ifscCode || 'N/A'}</Typography></Grid>
                </Grid>
              )}

              {/* Tab 3: Account - Password Management */}
              {tabValue === 3 && (
                <Box>
                  <Typography variant="h6" sx={{ mb: 2 }}>Account Information</Typography>
                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={6}>
                      <Paper sx={{ p: 2, bgcolor: '#f5f5f5' }}>
                        <Typography variant="caption" color="textSecondary">Registered Email</Typography>
                        <Typography variant="body1" fontWeight="bold">{selectedVendor.email || 'N/A'}</Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={6}>
                      <Paper sx={{ p: 2, bgcolor: '#f5f5f5' }}>
                        <Typography variant="caption" color="textSecondary">Registered Phone</Typography>
                        <Typography variant="body1" fontWeight="bold">{selectedVendor.phone || 'N/A'}</Typography>
                      </Paper>
                    </Grid>
                  </Grid>

                  <Divider sx={{ mb: 3 }} />

                  <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Lock fontSize="small" /> Change Password
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="New Password" type={showPassword ? 'text' : 'password'}
                        value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                                {showPassword ? <VisibilityOff /> : <VisibilityIcon />}
                              </IconButton>
                            </InputAdornment>
                          )
                        }} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label="Confirm Password" type={showPassword ? 'text' : 'password'}
                        value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
                        error={confirmPassword && newPassword !== confirmPassword}
                        helperText={confirmPassword && newPassword !== confirmPassword ? 'Passwords do not match' : ''} />
                    </Grid>
                    <Grid item xs={12}>
                      <Button variant="contained" color="primary" onClick={handleChangePassword}
                        disabled={passwordLoading || !newPassword || newPassword !== confirmPassword}
                        startIcon={passwordLoading ? <CircularProgress size={20} color="inherit" /> : <Lock />}>
                        {passwordLoading ? 'Changing...' : 'Change Password'}
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              )}

              {/* Tab 4: Full Profile */}
              {tabValue === 4 && (
                <Box>
                  <Typography variant="h6" sx={{ mb: 2 }}>All Vendor Fields</Typography>
                  {renderFullProfile(selectedVendor)}
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialog(false)}>Close</Button>
          {selectedVendor && (selectedVendor.kycStatus === 'PENDING' || selectedVendor.kycStatus === 'SUBMITTED') && (
            <>
              <Button variant="contained" color="success" onClick={() => { setDetailDialog(false); handleApproveKYC(selectedVendor); }}>Approve KYC</Button>
              <Button variant="contained" color="error" onClick={() => { setDetailDialog(false); handleRejectKYC(selectedVendor); }}>Reject KYC</Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Action Dialog */}
      <Dialog open={actionDialog.open} onClose={() => setActionDialog({ open: false, type: '', vendor: null })}>
        <DialogTitle>
          {actionDialog.type === 'approve' && 'Approve Vendor KYC'}
          {actionDialog.type === 'reject' && 'Reject Vendor KYC'}
          {actionDialog.type === 'suspend' && 'Suspend Vendor'}
          {actionDialog.type === 'activate' && 'Activate Vendor'}
        </DialogTitle>
        <DialogContent>
          {actionDialog.type === 'reject' ? (
            <>
              <Typography sx={{ mb: 2 }}>Select reason for rejection:</Typography>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Rejection Reason</InputLabel>
                <Select value={rejectReason} label="Rejection Reason" onChange={(e) => setRejectReason(e.target.value)}>
                  {rejectReasons.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                </Select>
              </FormControl>
              <TextField fullWidth multiline rows={2} label="Admin Notes (optional)"
                value={adminNotes} onChange={(e) => setAdminNotes(e.target.value)} />
            </>
          ) : (
            <>
              <Typography sx={{ mb: 2 }}>{actionDialog.type === 'delete' ? 'Are you sure you want to DELETE' : 'Are you sure you want to ' + actionDialog.type} <b>{actionDialog.vendor?.businessName}</b>?</Typography>
              <TextField fullWidth multiline rows={2} label="Admin Notes (optional)"
                value={adminNotes} onChange={(e) => setAdminNotes(e.target.value)} />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActionDialog({ open: false, type: '', vendor: null })} disabled={actionLoading}>Cancel</Button>
          <Button variant="contained"
            color={actionDialog.type === 'reject' || actionDialog.type === 'suspend' ? 'error' : 'success'}
            onClick={confirmAction}
            disabled={(actionDialog.type === 'reject' && !rejectReason) || actionLoading}>
            {actionLoading ? <CircularProgress size={24} color="inherit" /> : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default VendorManagement;
